import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Form, Card, Button, Breadcrumb, Table, Modal, Badge, Pagination } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAllBackdateRequests, updateBackdateRequestStatus } from "../../api/backdateRequest";

const ITEMS_PER_PAGE = 10;
const REASON_PREVIEW_LENGTH = 40;
const STATUS_VARIANT = { PENDING: "warning", APPROVED: "success", REJECTED: "danger" };

const fmtDate = (d) => {
    if (!d) return "-";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`;
};

const BackdateApprovalPage = () => {
    const token = localStorage.getItem("token");

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("PENDING");
    const [sortField, setSortField] = useState("latest");
    const [currentPage, setCurrentPage] = useState(1);

    const [showModal, setShowModal] = useState(false);
    const [selected, setSelected] = useState(null);
    const [action, setAction] = useState("");          // APPROVE | REJECT
    const [submitting, setSubmitting] = useState(false);

    const [showReasonModal, setShowReasonModal] = useState(false);
    const [reasonText, setReasonText] = useState("");

    const load = useCallback(() => {
        setLoading(true);
        getAllBackdateRequests(token)
            .then((r) => setRows(r.data || []))
            .catch(() => toast.error("Failed to load backdated requests."))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => { if (token) load(); }, [token, load]);

    let filtered = [...rows];
    if (statusFilter !== "ALL") filtered = filtered.filter((r) => r.status === statusFilter);
    if (search.trim()) {
        const q = search.trim().toLowerCase().replace(/[/.]/g, "-");
        filtered = filtered.filter((r) =>
            [
                r.employeeName,
                fmtDate(r.reportDate),
                fmtDate(r.createdAt),
                r.reason,
            ].some((v) => (v || "").toLowerCase().includes(q))
        );
    }
    if (sortField === "employee") filtered.sort((a, b) => (a.employeeName || "").localeCompare(b.employeeName || ""));
    else if (sortField === "employeeDesc") filtered.sort((a, b) => (b.employeeName || "").localeCompare(a.employeeName || ""));
    else filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    const openModal = (req, act) => { setSelected(req); setAction(act); setShowModal(true); };
    const closeModal = () => { setShowModal(false); setSelected(null); setAction(""); };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await updateBackdateRequestStatus(selected.id, action === "APPROVE" ? "APPROVED" : "REJECTED", token);
            toast.success(action === "APPROVE" ? "Request approved successfully" : "Request rejected successfully");
            closeModal();
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to process request.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container fluid className="p-4">
            <style>{`
        .lc-breadcrumb a { text-decoration: none !important; color: #2E5CB8 !important; }
        .lc-breadcrumb .breadcrumb-item.active { color: #6c757d !important; }
        .pagination .page-item.active .page-link { background-color: #2E5CB8 !important; border-color: #2E5CB8 !important; }
      `}</style>
            <ToastContainer position="top-right" autoClose={3000} />

            <h4 className="page-main-heading" style={{ marginBottom: 12 }}>Backdated Reporting</h4>
            <Breadcrumb className="lc-breadcrumb">
                <Breadcrumb.Item href="#">Reporting</Breadcrumb.Item>
                <Breadcrumb.Item active>Backdated Reporting</Breadcrumb.Item>
            </Breadcrumb>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                Requests from employees to fill daily reports older than the allowed backdate days.
            </p>

            <Row>
                <Col xl={12}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h5 className="mb-0">Backdated Reporting Requests</h5>
                            <Badge bg="secondary">Total: {filtered.length}</Badge>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3 justify-content-end align-items-center g-2">
                                <Col xs="auto">
                                    <div style={{ position: "relative", width: 320 }}>                                        <i
                                        className="bi bi-search"
                                        style={{
                                            position: "absolute", left: 12, top: "50%",
                                            transform: "translateY(-50%)", color: "#6c757d",
                                            fontSize: 14, pointerEvents: "none",
                                        }}
                                    ></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search employee / report date / reason"
                                            title="Search employee / report date / reason"
                                            value={search}
                                            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                                            style={{ width: "100%", paddingLeft: 34, fontSize: 14 }}
                                        />
                                    </div>
                                </Col>
                                <Col xs="auto">
                                    <Form.Select
                                        value={statusFilter}
                                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                        style={{ width: 160, fontSize: 14 }}
                                    >
                                        <option value="ALL">All Status</option>
                                        <option value="PENDING">Pending</option>
                                        <option value="APPROVED">Approved</option>
                                        <option value="REJECTED">Rejected</option>
                                    </Form.Select>
                                </Col>
                                <Col xs="auto">
                                    <Form.Select value={sortField} onChange={(e) => setSortField(e.target.value)} style={{ width: 200, fontSize: 14 }}>
                                        <option value="latest">Sort: Latest First</option>                                                                                
                                        <option value="employee">Sort: Employee (A–Z)</option>
                                        <option value="employeeDesc">Sort: Employee (Z–A)</option>
                                    </Form.Select>
                                </Col>
                            </Row>

                            <div style={{ maxHeight: 450, overflow: "auto" }}>
                                <Table bordered hover>
                                    <thead>
                                        <tr>
                                            {["Sr.No", "Employee", "Report Date", "Requested On", "Reason", "Status", "Action"].map((h) => (<th key={h} style={{ backgroundColor: "#2E5CB8", color: "white", position: "sticky", top: 0, zIndex: 1 }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && (
                                            <tr><td colSpan={7} className="text-center py-4"><div className="text-muted">Loading...</div></td></tr>)}
                                        {!loading && paginated.length === 0 && (
                                            <tr><td colSpan={7} className="text-center py-4"><div className="text-muted">No backdated reporting requests</div></td></tr>)}
                                        {!loading && paginated.map((r, index) => (
                                            <tr key={r.id}>
                                                <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                                <td>{r.employeeName || "-"}</td>
                                                <td>{fmtDate(r.reportDate)}</td>
                                                <td>{fmtDate(r.createdAt)}</td>
                                                <td style={{ maxWidth: 240 }}>
                                                    <div className="d-flex align-items-center">
                                                        <span
                                                            style={{ minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                                                            title={r.reason}
                                                        >
                                                            {r.reason || "-"}
                                                        </span>
                                                        {r.reason && r.reason.length > REASON_PREVIEW_LENGTH && (
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                className="p-0 ms-1 text-danger"
                                                                style={{ fontSize: 13, textDecoration: "none", whiteSpace: "nowrap" }}
                                                                onClick={() => { setReasonText(r.reason); setShowReasonModal(true); }}
                                                            >
                                                                Read more
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td><Badge bg={STATUS_VARIANT[r.status] || "secondary"}>{r.status}</Badge></td>
                                                <td style={{ whiteSpace: "nowrap" }}>
                                                    {r.status === "PENDING" ? (
                                                        <>
                                                            <Button variant="success" size="sm" className="me-1" style={{ padding: "3px 8px", fontSize: 12 }} onClick={() => openModal(r, "APPROVE")}>Approve</Button>
                                                            <Button variant="danger" size="sm" style={{ padding: "3px 8px", fontSize: 12 }} onClick={() => openModal(r, "REJECT")}>Reject</Button>
                                                        </>
                                                    ) : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            <div className="d-flex justify-content-end mt-3">
                                <Pagination>
                                    <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                                    {[...Array(totalPages).keys()].map((n) => (
                                        <Pagination.Item key={n + 1} active={n + 1 === currentPage} onClick={() => setCurrentPage(n + 1)}>{n + 1}</Pagination.Item>
                                    ))}
                                    <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages || totalPages === 0} />
                                </Pagination>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ── Approve / Reject confirmation ── */}
            <Modal show={showModal} onHide={closeModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {action === "APPROVE"
                            ? (<><i className="bi bi-check-circle-fill text-success me-2" />Approve Backdated Request</>)
                            : (<><i className="bi bi-x-circle-fill text-danger me-2" />Reject Backdated Request</>)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selected && (
                        <>
                            <div className="mb-3 p-3 rounded" style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", fontSize: 14 }}>
                                <Row><Col sm={4} className="text-muted fw-semibold">Employee:</Col><Col sm={8}>{selected.employeeName || "-"}</Col></Row>
                                <Row className="mt-1"><Col sm={4} className="text-muted fw-semibold">Report Date:</Col><Col sm={8}>{fmtDate(selected.reportDate)}</Col></Row>
                                <Row className="mt-1"><Col sm={4} className="text-muted fw-semibold">Requested On:</Col><Col sm={8}>{fmtDate(selected.createdAt)}</Col></Row>
                                <Row className="mt-1">
                                    <Col sm={4} className="text-muted fw-semibold">Reason:</Col>
                                    <Col sm={8} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 160, overflowY: "auto" }}>
                                        {selected.reason || "-"}
                                    </Col>
                                </Row>
                            </div>
                            <p className="mb-0" style={{ fontSize: 14 }}>
                                {action === "APPROVE"
                                    ? "The employee will be able to submit the daily report for this date."
                                    : "The employee will not be able to submit the daily report for this date."}
                            </p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={closeModal}>Cancel</Button>
                    <Button variant={action === "APPROVE" ? "success" : "danger"} size="sm" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? "Processing..." : action === "APPROVE" ? "Approve" : "Reject"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Full reason ── */}
            <Modal show={showReasonModal} onHide={() => setShowReasonModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Reason</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "60vh", overflowY: "auto" }}>
                    {reasonText}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowReasonModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </Container >
    );
};

export default BackdateApprovalPage;
